import React, { ChangeEvent, InputHTMLAttributes, useState } from "react";
import clsx from "clsx";

type FieldVariant = "default" | "underline";
export interface TextFieldV2Props extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  id?: string;
  type?: string;
  additionalWrapperClassNames?: string;
  additionalInputClassNames?: string;
  additionalLabelClassNames?: string;
  errorText?: boolean | string |  React.ReactNode;
  redirectText?: string;
  redirectOnClick?: (() => void) | null;
  value: any;
  name: string;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  trailingIconAdornment?: React.ReactNode;
  endAdornment?: React.ReactNode;
  startAdornment?: React.ReactNode;
  startAdornmentAfterInput?: React.ReactNode;
  placeholder?: string;
  tooltipText?: string;
  legend?: boolean;
  isDisabled?: boolean;
  required?: boolean;
  viewHeight?: string;
  variant?: FieldVariant;
}

export function TextFieldV2({
  label,
  id,
  errorText,
  redirectText = "",
  redirectOnClick,
  additionalWrapperClassNames,
  additionalInputClassNames,
  additionalLabelClassNames,
  type = "text",
  trailingIconAdornment,
  endAdornment,
  name,
  value,
  onChange,
  placeholder,
  tooltipText,
  legend,
  isDisabled,
  maxLength,
  startAdornment,
  startAdornmentAfterInput,
  required,
  viewHeight = "h-12",
  variant = "default",
  ...rest
}: TextFieldV2Props) {
  const [isTooltip, setIsTooltip] = useState<boolean>(false);

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (onChange) {
      if (maxLength && Number(event?.target?.value?.length) > Number(maxLength)) return;

      onChange(event);
    }
  };

  const wrapperDynamicClassNames = clsx(additionalWrapperClassNames, {
    "flex flex-col relative": true,
    "mt-2": startAdornment
  });

  const inputDynamicClassNames = clsx(`${additionalInputClassNames} ${viewHeight}`, {
    "w-full focus:outline-none dark:text-white":
    true,
  "border-gray-400 border-[1px] dark:bg-gray-900 focus:border-2 rounded-md rounded-tr-none pl-3 pr-6 placeholder-gray-600 hover:border-gray-600 focus:border-primary": variant === "default",
  "border-b-2 pt-4 dark:bg-background-primary": variant === "underline",
    "pl-[2.4rem]": startAdornment,
    "border-primary": variant === "underline" && value
  });

  const labelDynamicClassNames = clsx(
    additionalLabelClassNames,
    "absolute top-5 text-md flex items-center text-[#666666] dark:text-white dark:!bg-gray-900 h-[10px] hover:cursor-text",
    "transform transition-all group-focus-within:text-xs group-focus-within:-translate-y-[24px] group-focus-within:-translate-x-[4px] group-focus-within:py-0.5 group-focus-within:px-1 group-focus-within:bg-white group-focus-within:!text-primary group-focus-within:dark:bg-gray-900 group-focus-within: dark:text-white",
    {
      "text-primary": legend
    },
    {
      "left-3": variant === "default",
      "left-0": variant === "underline"
    },
    {
      "-translate-y-[24px] bg-white px-1 text-xs -translate-x-[4px] dark:bg-gray-900": value || legend || startAdornment
    }
  );

  return (
    <div className={wrapperDynamicClassNames}>
      {label && (
        <label htmlFor={id || name} className={labelDynamicClassNames} aria-label="Label-text">
          {label}
          {required && (
            <span className="text-red-500" aria-label="Required">
              &nbsp;*
            </span>
          )}
        </label>
      )}
      <div
        className="group flex flex-col item-center"
        onMouseEnter={() => setIsTooltip(true)}
        onMouseLeave={() => setIsTooltip(false)}
      >
        <input
          type={type}
          disabled={isDisabled}
          className={inputDynamicClassNames}
          name={name}
          onChange={handleInputChange}
          value={value}
          id={id || name}
          placeholder={placeholder}
          required={required}
          {...rest}
        />

        {trailingIconAdornment && (
          <div className="absolute right-1.5 top-4 flex items-center justify-center">{trailingIconAdornment}</div>
        )}

        {startAdornmentAfterInput && (
          <div className="absolute left-40 top-4 flex items-center justify-center">{startAdornmentAfterInput}</div>
        )}

        {startAdornment && (
          <div className="absolute left-3 top-4 flex items-center justify-center">{startAdornment}</div>
        )}

        {endAdornment && <div className="absolute right-4 top-1 flex items-center justify-center">{endAdornment}</div>}

        {errorText && (
          <div>
            <p className="text-typography-danger break-normal hyphens-manual text-[12px] pt-0 sm:pt-1 pb-1 sm:pb-0 w-full">{errorText}</p>
            {redirectText !== "" && redirectOnClick !== null && (
              <span
                data-testid="redirectText"
                className="text-typography-tertiary font-medium text-xs cursor-pointer ml-1"
                onClick={redirectOnClick}
              >
                {redirectText}
              </span>
            )}
          </div>
        )}
      </div>

      {tooltipText && isTooltip && (
        <div className="absolute left-1/2 transform -translate-x-1/2 top-[72px] bg-gray-700 text-white text-[10px] rounded shadow-md z-10 text-center w-max px-2 py-1.5">
          {tooltipText}
          <div className="absolute left-1/2 transform -translate-x-1/2 -top-1 w-0 h-0 border-l-4 border-l-transparent border-r-4 border-r-transparent border-b-4 border-b-gray-700" />
        </div>
      )}
    </div>
  );
}
